import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardPaste, Download, FileText, Save } from "lucide-react";

const EMPTY_ITEM = {
  box_no: "", product_code: "", description: "", batch_no: "",
  qty: 0, no_of_rolls: null, net_weight_kg: null, gross_weight_kg: null, remarks: "",
};

const EMPTY_SLIP = {
  slip_no: "", dispatch_date: "", vehicle_no: "", driver_name: "", driver_phone: "",
  transporter: "", lr_no: "", prepared_by: "", checked_by: "", notes: "", items: [],
};

/**
 * PackingSlipEditor — inline packing slip editor + PDF download.
 * Props: job (the full job doc), onSaved (optional callback)
 */
export default function PackingSlipEditor({ job, onSaved }) {
  const [slip, setSlip] = useState(EMPTY_SLIP);
  const [busy, setBusy] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteReplace, setPasteReplace] = useState(false);

  const load = () => {
    api.get(`/jobs/${job.id}/packing-slip`)
      .then(({ data }) => setSlip({ ...EMPTY_SLIP, ...data, items: data.items || [] }))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job?.id]);

  const set = (patch) => setSlip((s) => ({ ...s, ...patch }));
  const setItem = (idx, patch) => setSlip((s) => ({
    ...s, items: s.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
  }));
  const addItem = () => setSlip((s) => ({ ...s, items: [...s.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setSlip((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => {
    const t = { qty: 0, rolls: 0, net: 0, gross: 0, boxes: slip.items.length };
    for (const it of slip.items) {
      t.qty += Number(it.qty) || 0;
      t.rolls += Number(it.no_of_rolls) || 0;
      t.net += Number(it.net_weight_kg) || 0;
      t.gross += Number(it.gross_weight_kg) || 0;
    }
    return t;
  }, [slip.items]);

  const save = async () => {
    setBusy(true);
    try {
      // Coerce numeric fields
      const payload = {
        ...slip,
        items: slip.items.map((it, i) => ({
          ...it, sr_no: i + 1,
          qty: Number(it.qty) || 0,
          no_of_rolls: it.no_of_rolls === "" || it.no_of_rolls == null ? null : Number(it.no_of_rolls),
          net_weight_kg: it.net_weight_kg === "" || it.net_weight_kg == null ? null : Number(it.net_weight_kg),
          gross_weight_kg: it.gross_weight_kg === "" || it.gross_weight_kg == null ? null : Number(it.gross_weight_kg),
        })),
      };
      const { data } = await api.put(`/jobs/${job.id}/packing-slip`, payload);
      setSlip({ ...EMPTY_SLIP, ...data, items: data.items || [] });
      toast.success("Packing slip saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doPaste = async () => {
    if (!pasteText.trim()) { toast.error("Paste some rows first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/packing-slip/paste`, {
        pasted_text: pasteText, replace: pasteReplace,
      });
      toast.success(`Imported ${data.added} rows${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      setPasteOpen(false);
      setPasteText("");
      setPasteReplace(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      // Save first so PDF reflects current on-screen edits
      await save();
      const token = localStorage.getItem("erp_token");
      const res = await fetch(`${API_BASE}/jobs/${job.id}/packing-slip/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `packing-slip_${job.mpwo_number || job.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(`PDF error: ${e.message}`); }
    finally { setBusy(false); }
  };

  const previewPdf = async () => {
    const token = localStorage.getItem("erp_token");
    // Save first
    await save();
    const res = await fetch(`${API_BASE}/jobs/${job.id}/packing-slip/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const deliveryAddress = job.delivery_address || job.customer?.delivery_address || job.customer?.corporate_address || "—";

  return (
    <div className="space-y-4" data-testid="packing-slip-editor">
      {/* Delivery Address highlight */}
      <div className="border-2 border-[#FF5A00] bg-yellow-100 p-4" data-testid="packing-delivery-highlight">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="label-caps text-[10px] text-amber-900 mb-1">▼ SHIP TO / DELIVERY ADDRESS · Highlighted on PDF</div>
            <div className="text-neutral-900 font-semibold text-base whitespace-pre-line leading-relaxed">
              {deliveryAddress}
            </div>
            {job.delivery_address && job.customer?.delivery_address && job.delivery_address !== job.customer.delivery_address && (
              <div className="mt-1 text-[11px] text-amber-800 italic">Job override — differs from customer master</div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="label-caps text-[10px] text-amber-900">Location</div>
            <div className="font-mono-num text-sm font-semibold text-neutral-900">{job.location || "—"}</div>
          </div>
        </div>
      </div>

      {/* Slip metadata */}
      <div className="border border-neutral-200 bg-white p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Fld label="Slip No." value={slip.slip_no} onChange={(v) => set({ slip_no: v })} testid="pk-slip-no" placeholder={`PS-${new Date().getFullYear()}-001`} />
          <Fld label="Dispatch Date" type="date" value={slip.dispatch_date} onChange={(v) => set({ dispatch_date: v })} testid="pk-dispatch-date" />
          <Fld label="Vehicle No." value={slip.vehicle_no} onChange={(v) => set({ vehicle_no: v })} testid="pk-vehicle-no" placeholder="AP16-CX-4521" />
          <Fld label="LR No." value={slip.lr_no} onChange={(v) => set({ lr_no: v })} testid="pk-lr-no" />
          <Fld label="Transporter" value={slip.transporter} onChange={(v) => set({ transporter: v })} testid="pk-transporter" />
          <Fld label="Driver Name" value={slip.driver_name} onChange={(v) => set({ driver_name: v })} testid="pk-driver-name" />
          <Fld label="Driver Phone" value={slip.driver_phone} onChange={(v) => set({ driver_phone: v })} testid="pk-driver-phone" />
          <div />
          <Fld label="Prepared By" value={slip.prepared_by} onChange={(v) => set({ prepared_by: v })} testid="pk-prepared-by" />
          <Fld label="Checked By" value={slip.checked_by} onChange={(v) => set({ checked_by: v })} testid="pk-checked-by" />
        </div>
        <div className="mt-3">
          <label className="label-caps block mb-1 text-[10px]">Notes / Special Handling Instructions</label>
          <textarea
            data-testid="pk-notes"
            value={slip.notes || ""}
            onChange={(e) => set({ notes: e.target.value })}
            className="w-full border border-neutral-200 rounded-sm px-3 py-2 text-sm min-h-16"
            placeholder="e.g. Handle with care. Do not stack. Deliver during business hours only."
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="pk-add-row" onClick={addItem} variant="outline" className="rounded-sm gap-1.5">
          <Plus size={13} /> Add Row
        </Button>
        <Button data-testid="pk-paste-open" onClick={() => setPasteOpen(true)} variant="outline" className="rounded-sm gap-1.5">
          <ClipboardPaste size={13} /> Paste from Excel
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button data-testid="pk-save" onClick={save} disabled={busy} variant="outline" className="rounded-sm gap-1.5">
            <Save size={13} /> {busy ? "Saving…" : "Save"}
          </Button>
          <Button data-testid="pk-preview-pdf" onClick={previewPdf} disabled={busy} variant="outline" className="rounded-sm gap-1.5">
            <FileText size={13} /> Preview PDF
          </Button>
          <Button data-testid="pk-download-pdf" onClick={downloadPdf} disabled={busy} className="rounded-sm gap-1.5 bg-[#FF5A00] hover:bg-[#e04f00]">
            <Download size={13} /> Save &amp; Download PDF
          </Button>
        </div>
      </div>

      {/* Items table */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="pk-items-table">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-center px-2 py-2 w-10">Sr</th>
              <th className="text-left px-2 py-2 w-24">Box No.</th>
              <th className="text-left px-2 py-2 w-32">Product Code</th>
              <th className="text-left px-2 py-2">Description</th>
              <th className="text-left px-2 py-2 w-28">Batch No.</th>
              <th className="text-right px-2 py-2 w-24">Qty</th>
              <th className="text-right px-2 py-2 w-16">Rolls</th>
              <th className="text-right px-2 py-2 w-20">Net Wt</th>
              <th className="text-right px-2 py-2 w-20">Gross Wt</th>
              <th className="text-left px-2 py-2 w-32">Remarks</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {slip.items.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-neutral-400 text-sm">
                No items yet. Click <span className="font-medium">Add Row</span> to enter one manually, or <span className="font-medium">Paste from Excel</span> to bulk-import.
              </td></tr>
            )}
            {slip.items.map((it, i) => (
              <tr key={i} className="border-t border-neutral-100" data-testid={`pk-row-${i}`}>
                <td className="px-2 py-1 text-center font-mono-num text-neutral-500 text-xs">{i + 1}</td>
                <td className="px-2 py-1"><InlineIn value={it.box_no} onChange={(v) => setItem(i, { box_no: v })} testid={`pk-row-${i}-box`} placeholder="C-01" /></td>
                <td className="px-2 py-1"><InlineIn value={it.product_code} onChange={(v) => setItem(i, { product_code: v })} testid={`pk-row-${i}-product`} /></td>
                <td className="px-2 py-1"><InlineIn value={it.description} onChange={(v) => setItem(i, { description: v })} testid={`pk-row-${i}-desc`} /></td>
                <td className="px-2 py-1"><InlineIn value={it.batch_no} onChange={(v) => setItem(i, { batch_no: v })} testid={`pk-row-${i}-batch`} /></td>
                <td className="px-2 py-1"><InlineIn type="number" align="right" value={it.qty ?? ""} onChange={(v) => setItem(i, { qty: v })} testid={`pk-row-${i}-qty`} /></td>
                <td className="px-2 py-1"><InlineIn type="number" align="right" value={it.no_of_rolls ?? ""} onChange={(v) => setItem(i, { no_of_rolls: v })} testid={`pk-row-${i}-rolls`} /></td>
                <td className="px-2 py-1"><InlineIn type="number" step="0.01" align="right" value={it.net_weight_kg ?? ""} onChange={(v) => setItem(i, { net_weight_kg: v })} testid={`pk-row-${i}-net`} /></td>
                <td className="px-2 py-1"><InlineIn type="number" step="0.01" align="right" value={it.gross_weight_kg ?? ""} onChange={(v) => setItem(i, { gross_weight_kg: v })} testid={`pk-row-${i}-gross`} /></td>
                <td className="px-2 py-1"><InlineIn value={it.remarks} onChange={(v) => setItem(i, { remarks: v })} testid={`pk-row-${i}-remarks`} /></td>
                <td className="px-2 py-1 text-center">
                  <button
                    data-testid={`pk-row-${i}-delete`}
                    onClick={() => removeItem(i)}
                    className="p-1 text-neutral-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                    title="Remove row"
                  ><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {slip.items.length > 0 && (
              <tr className="bg-neutral-50 border-t-2 border-neutral-300 font-semibold" data-testid="pk-totals-row">
                <td colSpan={5} className="px-2 py-2 text-right label-caps">Total · {totals.boxes} box{totals.boxes !== 1 && "es"}</td>
                <td className="px-2 py-2 text-right font-mono-num">{totals.qty.toLocaleString("en-IN")}</td>
                <td className="px-2 py-2 text-right font-mono-num">{totals.rolls || ""}</td>
                <td className="px-2 py-2 text-right font-mono-num">{totals.net ? totals.net.toFixed(2) : ""}</td>
                <td className="px-2 py-2 text-right font-mono-num">{totals.gross ? totals.gross.toFixed(2) : ""}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paste dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Paste from Excel</DialogTitle>
            <DialogDescription>Bulk-import packing slip items from a spreadsheet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-neutral-600">
              Copy rows from Excel or Google Sheets and paste below. Expected column order:
              <div className="mt-1 border border-neutral-200 bg-neutral-50 p-2 font-mono-num text-[11px]">
                Box No · Product Code · Description · Batch No · Qty · Rolls · Net Wt · Gross Wt · Remarks
              </div>
              First row is auto-detected as header if it contains "Box No", "Sr", or "Batch".
            </div>
            <textarea
              data-testid="pk-paste-textarea"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="w-full border border-neutral-200 rounded-sm px-3 py-2 text-sm font-mono-num h-56"
              placeholder={`C-01\tLBL-001\tLBL Bimatoprost 0.03%\tB24001\t15000\t3\t12.5\t13.2\tFront\nC-02\tLBL-001\tLBL Bimatoprost 0.03%\tB24001\t15000\t3\t12.5\t13.2\t`}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid="pk-paste-replace"
                type="checkbox"
                checked={pasteReplace}
                onChange={(e) => setPasteReplace(e.target.checked)}
                className="accent-[#FF5A00]"
              />
              Replace existing items (default: append)
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="pk-paste-cancel" variant="outline" onClick={() => setPasteOpen(false)}>Cancel</Button>
            <Button data-testid="pk-paste-import" onClick={doPaste} disabled={busy} className="bg-[#FF5A00] hover:bg-[#e04f00]">
              {busy ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, value, onChange, testid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <Input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-sm h-8 text-sm"
      />
    </div>
  );
}

function InlineIn({ value, onChange, testid, type = "text", align = "left", step, placeholder }) {
  return (
    <input
      data-testid={testid}
      type={type}
      step={step}
      value={value == null ? "" : value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full border border-transparent hover:border-neutral-200 focus:border-[#FF5A00] focus:outline-none px-2 py-1 text-sm rounded-sm ${
        align === "right" ? "text-right font-mono-num" : ""
      }`}
    />
  );
}
